import React, { useState } from 'react';
import styled from 'styled-components';

import { v4 as uuidv4 } from 'uuid';
import { Command, State, tabActions } from '@store/index';
import { Card } from '@common/Card';
import { FindInPageOutlined } from '@mui/icons-material';
import { Divider, Stack, TextField } from '@mui/material';
import { Label } from '@common/Label';
import { BucketInput } from '@common/BucketInput/BucketInput';
import { useDispatch, useSelector } from 'react-redux';
import { Option } from '@common/BucketInput/BucketField';
import { useTheme } from '../../../../styles';
import { NodeInputExpression } from '../NodeInput';
import { NodeField, NodeIndex } from '@components/Flow/Nodes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const TTSearchType =
  'The search type specifies the method that the LLM will use to search the indexes.\n\nList will list high-level information about the documents returned from the search. This is useful for discovery of documents.\n\nDirect Search will not list the documents, but instead starts passing the full contents of the documents starting with the highest match. It will only stop when the token limit is reached.\n\nList & Search is the most effective method, as it will first list the documents for the LLM to read. The LLM will then ask to read a document from the list in full, and we will then pass the entire contents of the chosen document to the LLM to write a final response.';

export const TTForceSearch =
  'This option is only avaialble if the search method selected is List & Search.\n\nIf enabled, the LLM will be forced to search for a document before answering. If disabled, the LLM can answer the question with the list of documents only which only contains high level details.';

export const TTSearchTerm =
  'This is the search term that will be used for searching through your buckets.\n\nThere are two ways to inject a dynamic variable into this field:\n\n1. In the "Arguments" tab of this tool, create an argument and call it something like "search_term". Then add the argument in this field as a variable. For example: {{ tool.args.search_term }}.\n\n2. Use a variable from the data being passed to the completion node that this tool is attached to.\nFor example: {{ http.request.query.search_term }} ';

export const TTMaxTokens =
  'This is the maximum amount of tokens that the LLM can read from the buckets. This can be used with Max Results to achieve the desired token space. The more tokens you allow, the more files from your buckets the LLM can read. Some models are charged based on token usage, so the higher the max tokens, the higher the cost of executing this node.\n\nExample: Setting max results to 5 and max tokens to 3000 would mean the LLM can read up to 3000 tokens worth of content from the 5 results returned by the search. It will start reading the files with the highest certainty and continue until it runs out of tokens.';

export const TTMaxResults =
  'This is the maximum number of files that the LLM can read from the bucket. This can be used with Max Tokens to achieve the desired token space.\n\nExample: Setting max results to 5 and max tokens to 3000 would mean the LLM can read up to 3000 tokens worth of content from the 5 results. It will start reading the files with the highest certainty and continue until it runs out of tokens.';

export const TTBuckets =
  'Add a bucket to search using the search term outputted by the LLM.\n\nYou can use weights to give a particular bucket more priority over others. If your tool has already determined a particular domain, for example messaging, you might want to search a specific messaging bucket with a higher weight, and a generic support bucket with a lower weight. This means we expect the answer to come from the messaging bucket, but we have the generic support bucket to fall back to if we get poor/no results.';

export const TTBackupOutput = 'Specify the output of this tool for the event that a relevant document cannot be found.';

export const TTBackupToolChoice =
  'If a relevant document cannot be found, you can override the LLM tool choice field to direct the LLM how to behave after this happens.';

export const BucketSearch: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const theme = useTheme();

  const id = field.id;
  const state = command.state[field.id];

  const indexes = state.indexes as NodeIndex[];
  const maxResults = state.max_results as number;
  const maxTokens = state.max_tokens as number;
  const searchType = state.bucket_search_type;
  const searchDescriptor = state.search_descriptor;
  const backupOutput = state.backup_output;
  const backupToolChoice = state.backup_tool_choice;
  const enabled = state.enabled;

  const tool_names: any[] = commands
    .filter((command) => command.config.identifier === 'Tool')
    .map((tool) => tool?.state?.name || null)
    // .filter((name) => name && name !== command?.state?.name)
    .map((name) => ({ label: name, value: name }));

  const [collapsed, setCollapsed] = useState(!enabled);

  const toggleEnabled = (newVal: boolean) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );

    if (newVal) setCollapsed(false);
  };

  const searchTypeOptions: Option[] = [
    { label: 'List & Search', value: 'list_and_search' },
    { label: 'Direct Search', value: 'direct_search' },
    { label: 'List', value: 'list' },
  ];

  const addIndex = () => {
    const value = { id: uuidv4(), index: '', weight: 1 };

    dispatch(tabActions.setSaved(false));
    dispatch(
      tabActions.nodeInputNestedArrayPush({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'indexes',
        value,
      })
    );
  };

  const updateIndexField = (indexField: 'index' | 'weight' | 'type', indexFieldId: string, indexValue: string) => {
    dispatch(
      tabActions.nodeInputNestedArrayUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'indexes',
        fieldName: indexField,
        nestedFieldId: indexFieldId,
        value: indexValue,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const removeIndex = (nestedFieldId: string) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldName: 'indexes',
        nestedFieldId,
      })
    );
  };

  const body = (
    <Container>
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Search Term',
          tooltip: TTSearchTerm,
          placeholder: 'Term',
          value: searchDescriptor,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'search_descriptor',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={searchTypeOptions}
        override={{
          title: 'Search Method',
          tooltip: TTSearchType,
          placeholder: 'Select an index search type',
          value: searchType,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'bucket_search_type',
                fieldName,
                value,
              })
            );
          },
        }}
      />

      <Stack display='grid' gridTemplateColumns='1fr 1fr' gap='0.5rem'>
        <Stack spacing='8px'>
          <Label title='Max Tokens' tooltip={TTMaxTokens} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Tokens'
            value={maxTokens}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 0) return;

              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_tokens',
                  value: Number.parseInt(e.target.value),
                })
              );
            }}
          />
        </Stack>
        <Stack spacing='8px'>
          <Label title='Max Results' tooltip={TTMaxResults} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Results'
            value={maxResults}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 1) return;

              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_results',
                  value: Number.parseInt(e.target.value),
                })
              );
            }}
          />
        </Stack>
      </Stack>
      <Stack spacing='8px'>
        <Label title='Buckets' tooltip={TTBuckets} onClick={indexes.length !== 0 ? addIndex : undefined} />
        <BucketInput
          hideAddIndex
          indexes={indexes}
          addIndex={addIndex}
          updateField={updateIndexField}
          removeIndex={removeIndex}
        />
      </Stack>
      {backupOutput || backupToolChoice ? <Divider /> : null}
      {backupOutput && (
        <NodeInputExpression
          field={field}
          command={command}
          allowToolArgs
          override={{
            title: 'Backup Output',
            tooltip: TTBackupOutput,
            placeholder: 'There was no documentation found for the users question.',
            value: backupOutput,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'backup_output',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
      {backupToolChoice && (
        <NodeInputExpression
          field={field}
          command={command}
          options={[{ label: 'none', value: 'none' }, { label: 'auto', value: 'auto' }, ...tool_names]}
          override={{
            title: 'Backup Tool Choice',
            tooltip: TTBackupToolChoice,
            value: backupToolChoice,
            optionType: true,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'backup_tool_choice',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
    </Container>
  );

  return (
    <Card
      title='Bucket Search'
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      enabled={enabled}
      setEnabled={toggleEnabled}
      icon={<FindInPageOutlined sx={{ fontSize: '2.3ch', color: theme.telnyx.blue }} />}
      description={field.description}
      descriptionBorder
      body={body}
    />
  );
};
